import React from 'react';
import { Grid, Paper, Typography, Box, Card, CardContent, useTheme, alpha } from '@mui/material';
import { Line, Bar } from 'react-chartjs-2';
import { useThemeContext } from '../../context/ThemeContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { mode } = useThemeContext();
  // Adjust chart colors based on theme
  const isDark = mode === 'dark';

  // Sample data for charts
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const lineChartData = {
    labels: months,
    datasets: [
      {
        label: 'Active Companies',
        data: [65, 72, 78, 75, 82, 90, 94, 98, 102, 110, 118, 125],
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,
        tension: 0.4,
      },
      {
        label: 'Inactive Companies',
        data: [28, 26, 24, 22, 20, 18, 16, 14, 13, 12, 10, 8],
        borderColor: theme.palette.secondary.main,
        backgroundColor: theme.palette.secondary.main,
        tension: 0.4,
      },
    ],
  };

  const barChartData = {
    labels: ['Basic', 'Standard', 'Premium', 'Enterprise'],
    datasets: [
      {
        label: 'Companies by Plan',
        data: [12, 35, 27, 8],
        backgroundColor: [
          isDark ? 'rgba(59, 130, 246, 0.8)' : 'rgba(37, 99, 235, 0.8)',
          isDark ? 'rgba(96, 165, 250, 0.8)' : 'rgba(59, 130, 246, 0.8)',
          isDark ? 'rgba(52, 211, 153, 0.8)' : 'rgba(16, 185, 129, 0.8)',
          isDark ? 'rgba(110, 231, 183, 0.8)' : 'rgba(52, 211, 153, 0.8)',
        ],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: theme.palette.text.primary,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(37, 99, 235, 0.1)',
        },
      },
      y: {
        ticks: {
          color: theme.palette.text.secondary,
        },
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(37, 99, 235, 0.1)',
        },
      },
    },
  };

  // Summary cards data
  const summaryCards = [
    { title: 'Total Companies', value: '0', color: theme.palette.primary.main },
    { title: 'Active Plans', value: '0', color: theme.palette.success.main },
    { title: 'New Companies (This Month)', value: '0', color: theme.palette.info.main },
    { title: 'Total Revenue', value: '$0', color: theme.palette.secondary.main },
  ];


  return (
    <Box>
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 4, 
          fontWeight: 700,
          color: theme.palette.primary.main,
          position: 'relative',
          display: 'inline-block',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 40,
            height: 4,
            backgroundColor: theme.palette.secondary.main,
            borderRadius: 2,
          }
        }}
      >
        Dashboard Overview
      </Typography>
      
      <Grid container spacing={3}>
        {/* Summary Cards */}
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card 
              sx={{ 
                p: 1,
                height: '100%',
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                },
                border: `1px solid ${alpha(card.color, 0.2)}`,
                boxShadow: isDark 
                  ? `0 8px 24px ${alpha(card.color, 0.1)}`
                  : `0 6px 16px ${alpha(card.color, 0.08)}`,
              }}
            >
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Typography 
                  variant="h6" 
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.7),
                    fontWeight: 500,
                    mb: 1
                  }}
                >
                  {card.title}
                </Typography>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: card.color, 
                    fontWeight: 700,
                    letterSpacing: '-0.5px',
                  }}
                >
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Line Chart - Companies Over Time */}
        {/* <Grid item xs={12} md={8}>
          <Card 
            sx={{ 
              p: 0, 
              height: '100%',
              overflow: 'hidden',
              boxShadow: isDark 
                ? '0 8px 32px rgba(0, 0, 0, 0.1)'
                : '0 8px 32px rgba(37, 99, 235, 0.1)',
            }}
          >
            <Box 
              sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.1 : 0.5)}`,
                backgroundColor: isDark 
                  ? alpha(theme.palette.primary.dark, 0.2)
                  : alpha(theme.palette.primary.light, 0.1),
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: isDark ? theme.palette.primary.light : theme.palette.primary.dark
                }}
              >
                Companies Trend
              </Typography>
            </Box>
            <Box sx={{ p: 3, height: 350 }}>
              <Line data={lineChartData} options={chartOptions} />
            </Box>
          </Card>
        </Grid> */}

        {/* Bar Chart - Companies by Plan */}
        {/* <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              p: 0,
              height: '100%',
              overflow: 'hidden',
              boxShadow: isDark 
                ? '0 8px 32px rgba(0, 0, 0, 0.1)'
                : '0 8px 32px rgba(16, 185, 129, 0.1)',
              border: `1px solid ${alpha(theme.palette.secondary.main, isDark ? 0.1 : 0.2)}`,
            }}
          >
            <Box 
              sx={{ 
                p: 3, 
                borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.1 : 0.5)}`,
                backgroundColor: isDark 
                  ? alpha(theme.palette.secondary.dark, 0.2)
                  : alpha(theme.palette.secondary.light, 0.1),
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  color: isDark ? theme.palette.secondary.light : theme.palette.secondary.dark
                }}
              >
                Companies by Plan
              </Typography>
            </Box>
            <Box sx={{ p: 3, height: 350 }}>
              <Bar data={barChartData} options={chartOptions} />
            </Box>
          </Card>
        </Grid> */}
      </Grid>
    </Box>
  );
};

export default Dashboard;